/**
 * The Run scraper modal, opened from a brand's references page.
 *
 * Everything below the module line is driven by the descriptor rather than by a
 * table in this repo (memo §2/§4) — that is the point of `GET /scrapers`. In
 * particular the mode picker renders only when the module declared more than
 * one mode, and it never offers one the module did not declare: the extractor
 * validates `mode` against the same list and answers `400 unsupported_mode`,
 * and before that validation existed an undeclared mode was silently dropped
 * and a full-catalogue crawl ran in its place (1465 detail fetches for Rolex
 * instead of a handful).
 *
 * The one table this repo does keep is the mode WORDING (`modeLabel` /
 * `modeDescription`): `heritage` next to `full` says nothing to an operator
 * about which one crawls the discontinued models. The raw value still shows
 * beside the label, and an unlabelled mode still renders — the map is copy, not
 * a gate on what the picker offers.
 *
 * The catalogue fetch fails soft. If the service is unreachable — the machine
 * is asleep, the tunnel is down — the modal says so, and names the URL, rather
 * than rendering an empty form that looks like the brand has no scrapers.
 *
 * The one thing the descriptor is NOT trusted to lay out is its `description`:
 * that field is maintainer documentation, and it gets a first sentence here
 * with the rest behind an expander (see `ScraperDescription` below).
 *
 * Two phases in one modal: the form, then a status card for the job it started.
 * Starting a run used to close this window and jump to Catalogue → Extractor
 * Jobs, which took an operator who was mid-way through curating a brand and put
 * them in front of a cross-brand history table they had not asked for. The run
 * is a background job either way — so the modal now stays and reports on it,
 * with the jobs screen one deliberate click away rather than compulsory.
 */

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { ImportJobResultButton } from './ImportJobResultButton';
import {
  extractorErrorMessage,
  fetchBrandOverrides,
  fetchScrapers,
  runExtractorJob,
  subscribeToExtractorJob,
  type RunJobOptions,
  type ScraperDescriptor,
} from '../../services/extractorService';
import {
  modeDescription,
  modeLabel,
  modesForScraper,
  scrapersForBrand,
  shouldShowModePicker,
} from '../../services/extractorMatching';
import {
  hasMoreThanSummary,
  summarizeScraperDescription,
} from '../../services/extractorDescription';
import {
  formatDuration,
  isJobTerminal,
  jobErrorSnippet,
  jobResultCount,
  statusClasses,
  type ExtractorJobDoc,
} from '../../services/extractorJobs';

interface RunScraperModalProps {
  isOpen: boolean;
  onClose: () => void;
  brand: { id: string; name?: string };
  /** Opens the Extractor Jobs tab so the operator can watch the run. */
  onOpenJobs?: (brandId: string) => void;
  /** The finished run's result was imported — the caller's list is now stale. */
  onImported?: () => void;
}

/** Blank means "no limit" — the field is a safety valve, not a required input. */
function numberOrUndefined(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  const value = Number(trimmed);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

/**
 * Elapsed time, from the moment the run was submitted.
 *
 * Measured against the browser's own start rather than `startedAt`, because the
 * job doc may not be readable at all (its rule lives in watchlock) and a card
 * that cannot show progress should still show that time is passing.
 */
function elapsedSince(startedAtMs: number, now: number): string {
  return formatDuration(Math.max(0, now - startedAtMs));
}

/**
 * A module's description: one sentence here, the rest behind an expander.
 *
 * The descriptor's `description` is maintainer documentation — thousands of
 * characters of extraction internals, backticks and `<placeholder>` fragments
 * included — and rendering it whole put all of it between the module name and
 * the Limit box. It is still reachable, because when a run does something
 * surprising this is the only account of what the module actually does; it is
 * just no longer the loudest thing in the window. The full text keeps its own
 * line breaks and a scroll box, so it reads as the reference material it is
 * rather than as prose the modal is trying to tell you.
 */
function ScraperDescription({ description }: { description?: string }) {
  const summary = summarizeScraperDescription(description);
  if (summary === '') return null;

  return (
    <div className="mt-1">
      <p className="text-xs text-gray-500">{summary}</p>
      {hasMoreThanSummary(description) && (
        <details className="mt-1 group">
          <summary className="cursor-pointer text-xs text-brand-600 hover:text-brand-700 list-none">
            <span className="group-open:hidden">▸ About this scraper</span>
            <span className="hidden group-open:inline">▾ About this scraper</span>
          </summary>
          <div className="mt-1 max-h-40 overflow-y-auto rounded border border-gray-200 bg-gray-50 p-2 font-mono text-[11px] leading-relaxed text-gray-600 whitespace-pre-wrap break-words">
            {description}
          </div>
        </details>
      )}
    </div>
  );
}

export function RunScraperModal({
  isOpen,
  onClose,
  brand,
  onOpenJobs,
  onImported,
}: RunScraperModalProps) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [matches, setMatches] = useState<ScraperDescriptor[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mode, setMode] = useState<string>('full');
  const [limit, setLimit] = useState('');
  const [offset, setOffset] = useState('');
  const [concurrency, setConcurrency] = useState('');
  const [politenessDelayMs, setPolitenessDelayMs] = useState('');
  const [familySlugs, setFamilySlugs] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* Phase two: the job this modal started, and what is known about it. */
  const [startedJobId, setStartedJobId] = useState<string | null>(null);
  const [startedAtMs, setStartedAtMs] = useState<number | null>(null);
  const [job, setJob] = useState<ExtractorJobDoc | null>(null);
  const [jobWatchError, setJobWatchError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const selected = matches.find((m) => m.id === selectedId) ?? matches[0] ?? null;
  const finished = isJobTerminal(job);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      /*
       * Both, and in parallel: the modal has to resolve descriptor slugs exactly
       * as the button that opened it did, or a brand reachable only through an
       * override would open a modal offering nothing to run.
       */
      const [catalogue, overrides] = await Promise.all([
        fetchScrapers(),
        fetchBrandOverrides(),
      ]);
      const forBrand = scrapersForBrand(catalogue, brand, overrides);
      setMatches(forBrand);
      setSelectedId(forBrand[0]?.id ?? null);
      setMode(modesForScraper(forBrand[0])[0] ?? 'full');
    } catch (err) {
      setLoadError(extractorErrorMessage(err));
      setMatches([]);
    } finally {
      setLoading(false);
    }
  }, [brand]);

  useEffect(() => {
    if (isOpen) void load();
  }, [isOpen, load]);

  /*
   * Watch the job doc while the card is up. Live rather than polled — the runner
   * writes progress into the same document the jobs screen reads.
   *
   * A permission error here is expected until the `extraction_jobs` read rule
   * deploys from watchlock, so it degrades to a note and the link rather than to
   * a card that looks stuck at "queued" forever.
   */
  useEffect(() => {
    if (!isOpen || !startedJobId) return;
    setJobWatchError(null);
    const unsubscribe = subscribeToExtractorJob(
      startedJobId,
      (next) => setJob(next),
      (err) => setJobWatchError(extractorErrorMessage(err)),
    );
    return unsubscribe;
  }, [isOpen, startedJobId]);

  /* One tick a second, only while there is an unfinished run to time. */
  useEffect(() => {
    if (!isOpen || startedAtMs === null || finished) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [isOpen, startedAtMs, finished]);

  /* A different module may declare a different mode set — never carry one over. */
  useEffect(() => {
    if (selected) setMode(modesForScraper(selected)[0] ?? 'full');
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRun = async () => {
    if (!selected) return;

    const options: RunJobOptions = {};
    /*
     * `mode` is sent only when the module declared more than one. A module with
     * a single mode has no choice to express, and sending its default back
     * would put a value on the wire that the descriptor is already the
     * authority on.
     */
    if (shouldShowModePicker(selected)) options.mode = mode;

    const limitValue = numberOrUndefined(limit);
    if (limitValue !== undefined) options.limit = limitValue;
    const offsetValue = numberOrUndefined(offset);
    if (offsetValue !== undefined) options.offset = offsetValue;
    const concurrencyValue = numberOrUndefined(concurrency);
    if (concurrencyValue !== undefined) options.concurrency = concurrencyValue;
    const delayValue = numberOrUndefined(politenessDelayMs);
    if (delayValue !== undefined) options.politenessDelayMs = delayValue;

    const families = familySlugs
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s !== '');
    if (families.length > 0) options.familySlugs = families;

    setSubmitting(true);
    try {
      const { jobId } = await runExtractorJob({
        extractorId: selected.id,
        brandId: brand.id,
        /*
         * The brand doc id IS the slug in this admin — `WatchBrand` has no
         * separate slug field — and the extractor takes what it is given
         * (memo §7: the admin's brand picker is ground truth).
         */
        brandSlug: brand.id,
        options,
      });
      /*
       * No navigation. The window becomes the status card for this job; the
       * operator goes to the jobs screen when they choose to, not because
       * starting a run moved them there.
       */
      setStartedJobId(jobId);
      setStartedAtMs(Date.now());
      setNow(Date.now());
      setJob(null);
      toast.success(`Job ${jobId} started`);
    } catch (err) {
      toast.error(extractorErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const goToJobs = () => {
    onClose();
    onOpenJobs?.(brand.id);
  };

  /**
   * Closing is always allowed — the crawl is a server-side job and this window
   * has never been what keeps it alive. Leaving mid-run says so, with the way
   * back, because a card that simply vanishes reads as a cancelled run.
   */
  const handleClose = () => {
    if (startedJobId && !finished) {
      toast(
        (t) => (
          <span className="text-sm">
            Job still running — check its progress in Extractor Jobs.
            {onOpenJobs && (
              <button
                type="button"
                className="ml-2 text-brand-600 hover:text-brand-700 underline"
                onClick={() => {
                  toast.dismiss(t.id);
                  onOpenJobs(brand.id);
                }}
              >
                Open
              </button>
            )}
          </span>
        ),
        { duration: 6000 },
      );
    }
    setStartedJobId(null);
    setStartedAtMs(null);
    setJob(null);
    setJobWatchError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Run scraper — ${brand.name ?? brand.id}`}
      size="md"
    >
      {startedJobId ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-gray-800">Job started</div>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses(
                  job?.status,
                )}`}
              >
                {job?.status ?? 'starting…'}
              </span>
            </div>

            <div className="text-xs text-gray-500 font-mono break-all">{startedJobId}</div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
              <span>
                Elapsed{' '}
                <span className="font-medium text-gray-800">
                  {startedAtMs === null ? '—' : elapsedSince(startedAtMs, now)}
                </span>
              </span>
              {jobResultCount(job ?? { jobId: startedJobId }) !== null && (
                <span>
                  References{' '}
                  <span className="font-medium text-gray-800">
                    {jobResultCount(job ?? { jobId: startedJobId })}
                  </span>
                </span>
              )}
              {job?.progressLabel && <span className="text-gray-500">{job.progressLabel}</span>}
            </div>

            {job && jobErrorSnippet(job) && (
              <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-800">
                {jobErrorSnippet(job)}
              </div>
            )}

            {jobWatchError && (
              <p className="text-xs text-amber-700">
                Live status is unavailable from here ({jobWatchError}). The run is unaffected —
                follow it in Extractor Jobs.
              </p>
            )}
          </div>

          <p className="text-xs text-gray-500">
            {job?.status === 'completed'
              ? 'The run has finished. Import it straight into the bulk-import wizard — the same envelope the upload box takes, without the download.'
              : finished
                ? 'The run has finished. Its result file is downloadable from Extractor Jobs.'
                : 'The run continues on the extractor whether or not this window is open — a brand crawl takes minutes to tens of minutes.'}
          </p>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            {onOpenJobs && (
              <Button variant="secondary" onClick={goToJobs}>
                View in Extractor Jobs →
              </Button>
            )}
            {job?.status === 'completed' && (
              <ImportJobResultButton
                jobId={startedJobId}
                brandId={brand.id}
                size="md"
                onImported={() => {
                  onImported?.();
                  handleClose();
                }}
              />
            )}
            <Button variant={job?.status === 'completed' ? 'secondary' : 'primary'} onClick={handleClose}>
              {finished ? 'Done' : 'Close'}
            </Button>
          </div>
        </div>
      ) : loading ? (
        <div className="text-sm text-gray-500">Loading the scraper catalogue…</div>
      ) : loadError ? (
        <div className="space-y-3">
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            <div className="font-medium">Extractor not reachable</div>
            <div className="mt-1 text-xs">{loadError}</div>
          </div>
          <p className="text-xs text-gray-500">
            Check the service is running, then retry. The URL it is being asked for is in
            Configuration → Extractor Service.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
            <Button onClick={() => void load()}>Retry</Button>
          </div>
        </div>
      ) : matches.length === 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            No registered scraper lists <code className="px-1 bg-gray-100 rounded">{brand.id}</code>{' '}
            among the brands it can crawl.
          </p>
          <div className="flex justify-end">
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700">Module</div>
            {matches.length === 1 ? (
              <div className="mt-1">
                <div className="text-sm text-gray-900">
                  {selected?.displayName}{' '}
                  <span className="text-xs text-gray-500">v{selected?.version}</span>
                </div>
                <ScraperDescription description={selected?.description} />
              </div>
            ) : (
              <>
                <select
                  value={selected?.id ?? ''}
                  onChange={(e) => setSelectedId(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                >
                  {matches.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.displayName} (v{m.version})
                    </option>
                  ))}
                </select>
                <ScraperDescription description={selected?.description} />
              </>
            )}
          </div>

          {selected && shouldShowModePicker(selected) && (
            <div>
              <div className="text-sm font-medium text-gray-700 mb-1">Mode</div>
              <div className="flex flex-col gap-2">
                {modesForScraper(selected).map((m) => (
                  <label key={m} className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="radio"
                      name="extractor-mode"
                      value={m}
                      checked={mode === m}
                      onChange={() => setMode(m)}
                      className="mt-1 text-brand-600 focus:ring-brand-500"
                    />
                    <span>
                      <span className="font-medium">{modeLabel(m)}</span>{' '}
                      <code className="px-1 bg-gray-100 rounded text-xs text-gray-500">{m}</code>
                      {modeDescription(m) && (
                        <span className="block text-xs text-gray-500">{modeDescription(m)}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="scraper-limit">
              Limit
            </label>
            <input
              id="scraper-limit"
              type="number"
              min={0}
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="Blank = whole catalogue"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              The safety valve for a first run against a brand — start at 5.
            </p>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              className="text-sm text-brand-600 hover:text-brand-700"
            >
              {showAdvanced ? '▾' : '▸'} Advanced
            </button>
            {showAdvanced && (
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Offset</label>
                  <input
                    type="number"
                    min={0}
                    value={offset}
                    onChange={(e) => setOffset(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Concurrency</label>
                  <input
                    type="number"
                    min={0}
                    value={concurrency}
                    onChange={(e) => setConcurrency(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Politeness delay (ms)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={politenessDelayMs}
                    onChange={(e) => setPolitenessDelayMs(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Family slugs
                  </label>
                  <input
                    type="text"
                    value={familySlugs}
                    onChange={(e) => setFamilySlugs(e.target.value)}
                    placeholder="comma separated"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500">
            The run starts immediately and continues on the extractor whether or not this window is
            open — a brand crawl takes minutes to tens of minutes. Progress is in Catalogue →
            Extractor Jobs.
          </p>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleRun} loading={submitting} disabled={submitting || !selected}>
              Run
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
